import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  TextField,
  Button,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Chip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
  RadioGroup,
  Radio,
  FormControl,
  Divider
} from '@mui/material';
import {
  HowToReg as RegisterIcon,
  SportsSoccer as SoccerIcon
} from '@mui/icons-material';
import DOMPurify from 'dompurify';
import unifiedApi from '../../utils/unifiedApi';
import { useAuth } from '../../contexts/AuthContext';
import EmergencyContactFields from '../../components/formItems/EmergencyContactFields';
import { EMERGENCY_CONTACT_PHONE_ERROR, isValidEmergencyContactPhone } from '../../utils/emergencyContact';

const POSITION_TYPE_LABELS = {
  OutfieldPlayer: 'Outfield Player',
  Goalkeeper: 'Goalkeeper',
  SpareOutfielder: 'Spare Outfielder',
  SpareGoalkeeper: 'Spare Goalkeeper'
};

const JERSEY_SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'];

const PLAYING_POSITIONS = ['Striker', 'Midfield', 'Defender', 'Keeper'];

const MIN_DOB = '1900-01-01';
const MIN_AGE_YEARS = 16;

const getMaxDobForMinAge = (minAgeYears) => {
  const now = new Date();
  const year = now.getFullYear() - minAgeYears;
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatMoney = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

const loadSquareSdk = (sandbox) => new Promise((resolve, reject) => {
  if (window.Square) { resolve(window.Square); return; }
  const src = sandbox
    ? 'https://sandbox.web.squarecdn.com/v1/square.js'
    : 'https://web.squarecdn.com/v1/square.js';
  const existing = document.querySelector(`script[src="${src}"]`);
  if (existing) {
    existing.addEventListener('load', () => resolve(window.Square));
    existing.addEventListener('error', reject);
    return;
  }
  const script = document.createElement('script');
  script.src = src;
  script.onload = () => resolve(window.Square);
  script.onerror = reject;
  document.head.appendChild(script);
});

const RegistrationPage = () => {
  const { leagueSlug } = useParams();
  const [searchParams] = useSearchParams();
  const earlyAccessToken = searchParams.get('earlyAccess');
  const { isAuthenticated, isLoading: authLoading, logout } = useAuth();
  const maxDob = useRef(getMaxDobForMinAge(MIN_AGE_YEARS));
  const [config, setConfig] = useState(null);
  const sanitizedBlurb = useMemo(() => config?.blurb ? DOMPurify.sanitize(config.blurb) : '', [config?.blurb]);
  const sanitizedWarning = useMemo(() => config?.paymentWarningMessage ? DOMPurify.sanitize(config.paymentWarningMessage) : '', [config?.paymentWarningMessage]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [warningOpen, setWarningOpen] = useState(false);
  const [prefilled, setPrefilled] = useState(false);
  const [profileFirstName, setProfileFirstName] = useState('');
  const [hasUserTenant, setHasUserTenant] = useState(false);
  const [differentEmailDialogOpen, setDifferentEmailDialogOpen] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('full');
  const [confirmed, setConfirmed] = useState(false);
  const [squareError, setSquareError] = useState(null);
  const [squareReady, setSquareReady] = useState(false);
  const cardContainerRef = useRef(null);
  const squareCardRef = useRef(null);

  const [form, setForm] = useState({
    email: '',
    confirmEmail: '',
    firstName: '',
    lastName: '',
    birthday: '',
    phone: '',
    postalCode: '',
    jerseySize: '',
    emergencyContactName: '',
    emergencyContactPhone: '',
    preferredPosition1: '',
    preferredPosition2: '',
    positionType: '',
    expectedGamesToMiss: 0,
    isNewToLeague: 'no',
    referral: '',
    waiverAccepted: false
  });

  useEffect(() => {
    const loadConfig = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await unifiedApi.getPublicRegistrationConfig(leagueSlug, earlyAccessToken);
        if (!data) {
          setError('Registration is not available for this league.');
          return;
        }
        setConfig(data);
      } catch (err) {
        setError('Failed to load registration information.');
        console.error('Error loading registration config:', err);
      } finally {
        setLoading(false);
      }
    };

    if (leagueSlug) {
      loadConfig();
    }
  }, [leagueSlug, earlyAccessToken]);

  // Prefill form from authenticated user's profile
  useEffect(() => {
    const prefillFromProfile = async () => {
      if (!isAuthenticated || authLoading) return;
      try {
        const profile = await unifiedApi.getMe();
        if (!profile) return;

        const hasExistingTenant = profile.userTenantId != null;
        setHasUserTenant(hasExistingTenant);
        setProfileFirstName(profile.firstName || '');

        const formatBirthDate = (dateStr) => {
          if (!dateStr) return '';
          const d = new Date(dateStr);
          if (isNaN(d.getTime())) return '';
          const year = d.getUTCFullYear();
          const month = String(d.getUTCMonth() + 1).padStart(2, '0');
          const day = String(d.getUTCDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        };

        // Only prefill fields the user hasn't touched (still at initial values)
        setForm(prev => ({
          ...prev,
          email: prev.email === '' ? (profile.email || '') : prev.email,
          confirmEmail: prev.confirmEmail === '' ? (profile.email || '') : prev.confirmEmail,
          firstName: prev.firstName === '' ? (profile.firstName || '') : prev.firstName,
          lastName: prev.lastName === '' ? (profile.lastName || '') : prev.lastName,
          birthday: prev.birthday === '' ? (formatBirthDate(profile.birthDate) || '') : prev.birthday,
          phone: prev.phone === '' ? (profile.phoneNumber || '') : prev.phone,
          postalCode: prev.postalCode === '' ? (profile.postalCode || '') : prev.postalCode,
          jerseySize: prev.jerseySize === '' ? (profile.jerseySize || '') : prev.jerseySize,
          emergencyContactName: prev.emergencyContactName === '' ? (profile.emergencyContactName || '') : prev.emergencyContactName,
          emergencyContactPhone: prev.emergencyContactPhone === '' ? (profile.emergencyContactPhone || '') : prev.emergencyContactPhone,
          preferredPosition1: prev.preferredPosition1 === '' ? (profile.preferredPosition1 || '') : prev.preferredPosition1,
          preferredPosition2: prev.preferredPosition2 === '' ? (profile.preferredPosition2 || '') : prev.preferredPosition2,
          ...(hasExistingTenant ? { isNewToLeague: 'no' } : {})
        }));
        setPrefilled(true);
      } catch (err) {
        // Profile fetch failed — user can still fill form manually
        console.error('Error loading profile for prefill:', err);
      }
    };

    prefillFromProfile();
  }, [isAuthenticated, authLoading]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleBirthdayChange = (e) => {
    const value = e.target.value;
    // Validate YYYY-MM-DD format — reject years longer than 4 digits
    if (value && !/^\d{0,4}(-\d{0,2}(-\d{0,2})?)?$/.test(value)) {
      return;
    }
    setForm(prev => ({ ...prev, birthday: value }));
  };

  const isValidCalendarDate = (dateString) => {
    if (!dateString) return false;
    const regex = /^\d{4}-\d{2}-\d{2}$/;
    if (!regex.test(dateString)) return false;
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return false;
    // Check the date components match (catches invalid dates like Feb 30)
    const [year, month, day] = dateString.split('-').map(Number);
    if (date.getUTCFullYear() !== year || date.getUTCMonth() + 1 !== month || date.getUTCDate() !== day) return false;
    return true;
  };

  // Returns a user-facing error message for the birthday field, or null if valid.
  const getBirthdayError = (dateString) => {
    if (!dateString) return null;
    if (!isValidCalendarDate(dateString)) return 'Please enter a valid date (YYYY-MM-DD).';
    if (dateString < MIN_DOB) return 'Please enter a valid date of birth.';
    if (dateString > maxDob.current) return `You must be at least ${MIN_AGE_YEARS} years old to register.`;
    return null;
  };

  const birthdayError = getBirthdayError(form.birthday);
  const isValidBirthday = form.birthday !== '' && birthdayError === null;
  const emailMismatch = form.confirmEmail !== '' && form.email.toLowerCase() !== form.confirmEmail.toLowerCase();
  const birthdayInvalid = form.birthday !== '' && birthdayError !== null;

  const selectedPosition = config?.positions?.find(p => p.positionType === form.positionType) || null;
  const installmentsOfferable = !!(config?.installmentsAvailable && selectedPosition
    && selectedPosition.priceInCents >= config.installmentMinimumAmountCents);

  // Reset to full payment if the current selection no longer qualifies for installments.
  useEffect(() => {
    if (!installmentsOfferable && paymentMethod === 'installments') setPaymentMethod('full');
  }, [installmentsOfferable, paymentMethod]);

  // Load and mount the Square Web Payments SDK card form when installments are chosen.
  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      if (paymentMethod !== 'installments' || !config?.installmentsAvailable) return;
      if (!config.squareApplicationId || !config.squareLocationId) {
        setSquareError('Online card payment is not configured for this organization.');
        return;
      }
      try {
        setSquareError(null);
        setSquareReady(false);
        const Square = await loadSquareSdk(config.squareSandbox);
        if (cancelled) return;
        const payments = Square.payments(config.squareApplicationId, config.squareLocationId);
        const card = await payments.card();
        if (cancelled) return;
        await card.attach(cardContainerRef.current);
        squareCardRef.current = card;
        setSquareReady(true);
      } catch (err) {
        if (!cancelled) setSquareError('Could not load the secure card form. Please try again.');
        console.error('Square SDK error:', err);
      }
    };
    init();
    return () => {
      cancelled = true;
      if (squareCardRef.current) {
        try { squareCardRef.current.destroy(); } catch { /* ignore */ }
        squareCardRef.current = null;
      }
    };
  }, [paymentMethod, config]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (emailMismatch) {
      setSubmitError('Email addresses do not match.');
      return;
    }
    if (!isValidBirthday) {
      setSubmitError(birthdayError || 'Please enter a valid date of birth.');
      return;
    }
    if (!form.emergencyContactName.trim()) {
      setSubmitError('Emergency contact name is required.');
      return;
    }
    if (!isValidEmergencyContactPhone(form.emergencyContactPhone)) {
      setSubmitError(EMERGENCY_CONTACT_PHONE_ERROR);
      return;
    }
    if (sanitizedWarning) {
      setWarningOpen(true);
      return;
    }
    await submitRegistration();
  };

  const submitRegistration = async () => {
    setSubmitting(true);
    setSubmitError(null);

    try {
      let cardNonce;
      if (paymentMethod === 'installments') {
        if (!squareCardRef.current) {
          setSubmitError('The card form is still loading. Please wait a moment and try again.');
          setSubmitting(false);
          return;
        }
        const tokenResult = await squareCardRef.current.tokenize();
        if (tokenResult.status !== 'OK') {
          setSubmitError('Please check your card details and try again.');
          setSubmitting(false);
          return;
        }
        cardNonce = tokenResult.token;
      }

      const submitData = {
        ...form,
        birthday: form.birthday ? new Date(form.birthday).toISOString() : undefined,
        expectedGamesToMiss: parseInt(form.expectedGamesToMiss, 10) || 0,
        positionType: form.positionType,
        isNewToLeague: form.isNewToLeague === 'yes',
        referral: form.isNewToLeague === 'yes' ? form.referral : undefined,
        useInstallments: paymentMethod === 'installments',
        cardNonce
      };
      delete submitData.confirmEmail;

      const result = await unifiedApi.submitRegistration(leagueSlug, submitData, earlyAccessToken);

      if (result?.confirmed) {
        setConfirmed(true);
        return;
      }

      if (result?.sessionToken) {
        localStorage.setItem('registrationSessionToken', result.sessionToken);
      }

      if (result?.checkoutUrl) {
        window.location.href = result.checkoutUrl;
      }
    } catch (err) {
      const message = err?.message || 'Failed to submit registration.';
      setSubmitError(message);
      console.error('Registration submit error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box mt={4} px={2}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (confirmed) {
    return (
      <Box mt={4} px={2}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <RegisterIcon sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>Registration complete</Typography>
          <Typography variant="body1" color="text.secondary">
            Your first payment was processed and your installment plan is set up. A receipt has been emailed to you,
            and future payments will be charged automatically.
          </Typography>
        </Paper>
      </Box>
    );
  }

  if (!config?.registrationOpen) {
    return (
      <Box mt={4} px={2}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <RegisterIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Registration Closed
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Registration for {config?.leagueName || 'this league'} is not currently open.
          </Typography>
        </Paper>
      </Box>
    );
  }

  const availablePositions = config.positions?.filter(p => !p.soldOut) || [];

  return (
    <Box>
      <Box className="page-header">
        <Typography variant="h4" component="h1" className="page-title">
          <SoccerIcon color="primary" />
          Register for {config.leagueName}
        </Typography>
      </Box>

      {config.earlyAccess && (
        <Alert severity="info" sx={{ mb: 3 }}>
          You&apos;re using an early-access registration link. General registration for this league isn&apos;t open yet.
        </Alert>
      )}

      {config.blurb && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box
            sx={{
              '& p': { mt: 0, mb: 1 },
              '& p:last-child': { mb: 0 },
              '& ul, & ol': { mt: 0, mb: 1, pl: 3 },
              '& a': { color: 'primary.main' },
            }}
            dangerouslySetInnerHTML={{ __html: sanitizedBlurb }}
          />
        </Paper>
      )}

      {/* Position Availability */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Available Positions</Typography>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          {config.positions?.map((pos) => (
            <Chip
              key={pos.positionType}
              label={`${POSITION_TYPE_LABELS[pos.positionType] || pos.positionType}: ${pos.availableSlots} spots — $${(pos.priceInCents / 100).toFixed(2)}`}
              color={pos.soldOut ? 'default' : 'primary'}
              variant={pos.soldOut ? 'outlined' : 'filled'}
              disabled={pos.soldOut}
            />
          ))}
        </Stack>
      </Paper>

      {submitError && (
        <Alert severity="error" sx={{ mb: 3 }}>{submitError}</Alert>
      )}

      {prefilled && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Welcome back, {profileFirstName}! We&apos;ve filled in your details from your profile.{' '}
          <Link
            component="button"
            variant="body2"
            onClick={() => setDifferentEmailDialogOpen(true)}
            sx={{ verticalAlign: 'baseline' }}
          >
            Want to use a different email?
          </Link>
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        {/* Personal Information */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Personal Information</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <TextField
              name="firstName"
              label="First Name"
              value={form.firstName}
              onChange={handleChange}
              required
              fullWidth
            />
            <TextField
              name="lastName"
              label="Last Name"
              value={form.lastName}
              onChange={handleChange}
              required
              fullWidth
            />
            <TextField
              name="email"
              label="Email"
              type="email"
              value={form.email}
              onChange={handleChange}
              required
              fullWidth
              slotProps={prefilled ? { htmlInput: { readOnly: true } } : {}}
              sx={prefilled ? { '& .MuiInputBase-input': { color: 'text.secondary' } } : {}}
            />
            <TextField
              name="confirmEmail"
              label="Confirm Email"
              type="email"
              value={form.confirmEmail}
              onChange={handleChange}
              required
              fullWidth
              slotProps={prefilled ? { htmlInput: { readOnly: true } } : {}}
              sx={prefilled ? { '& .MuiInputBase-input': { color: 'text.secondary' } } : {}}
              error={emailMismatch}
              helperText={emailMismatch ? 'Email addresses do not match' : ''}
            />
            <TextField
              name="phone"
              label="Phone"
              value={form.phone}
              onChange={handleChange}
              required
              fullWidth
            />
            <TextField
              name="birthday"
              label="Date of Birth"
              type="date"
              value={form.birthday}
              onChange={handleBirthdayChange}
              required
              fullWidth
              error={birthdayInvalid}
              helperText={birthdayInvalid ? birthdayError : ''}
              slotProps={{
                inputLabel: { shrink: true },
                htmlInput: { min: MIN_DOB, max: maxDob.current }
              }}
            />
            <TextField
              name="postalCode"
              label="Postal Code"
              value={form.postalCode}
              onChange={handleChange}
              required
              fullWidth
            />
            <TextField
              name="jerseySize"
              label="Jersey Size"
              select
              value={form.jerseySize}
              onChange={handleChange}
              required
              fullWidth
            >
              {JERSEY_SIZES.map(size => (
                <MenuItem key={size} value={size}>{size}</MenuItem>
              ))}
            </TextField>
            {!(prefilled && hasUserTenant) && (
              <TextField
                name="isNewToLeague"
                label="Are you new to the league?"
                select
                value={form.isNewToLeague}
                onChange={handleChange}
                required
                fullWidth
              >
                <MenuItem value="no">No</MenuItem>
                <MenuItem value="yes">Yes</MenuItem>
              </TextField>
            )}
            {form.isNewToLeague === 'yes' && (
              <TextField
                name="referral"
                label="Who referred you?"
                value={form.referral}
                onChange={handleChange}
                required
                fullWidth
              />
            )}
          </Box>
        </Paper>

        <EmergencyContactFields
          name={form.emergencyContactName}
          phone={form.emergencyContactPhone}
          onChange={handleChange}
        />

        {/* Soccer Details */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Soccer Details</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <TextField
              name="positionType"
              label="Registration Type"
              select
              value={form.positionType}
              onChange={handleChange}
              required
              fullWidth
            >
              {availablePositions.map(pos => (
                <MenuItem key={pos.positionType} value={pos.positionType}>
                  {POSITION_TYPE_LABELS[pos.positionType] || pos.positionType} — ${(pos.priceInCents / 100).toFixed(2)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              name="expectedGamesToMiss"
              label="Expected Games to Miss"
              type="number"
              value={form.expectedGamesToMiss}
              onChange={handleChange}
              fullWidth
              slotProps={{ htmlInput: { min: 0, max: 100 } }}
            />
            <TextField
              name="preferredPosition1"
              label="Preferred Position"
              select
              value={form.preferredPosition1}
              onChange={handleChange}
              required
              fullWidth
            >
              {PLAYING_POSITIONS.map(pos => (
                <MenuItem key={pos} value={pos}>{pos}</MenuItem>
              ))}
            </TextField>
            <TextField
              name="preferredPosition2"
              label="Second Preferred Position"
              select
              value={form.preferredPosition2}
              onChange={handleChange}
              required
              fullWidth
            >
              {PLAYING_POSITIONS.map(pos => (
                <MenuItem key={pos} value={pos}>{pos}</MenuItem>
              ))}
            </TextField>
          </Box>
        </Paper>

        {/* Waiver */}
        {config.waiverText && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>Waiver</Typography>
            <Paper variant="outlined" sx={{ p: 2, maxHeight: 200, overflow: 'auto', mb: 2, bgcolor: 'grey.50' }}>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {config.waiverText}
              </Typography>
            </Paper>
            <FormControlLabel
              control={
                <Checkbox
                  name="waiverAccepted"
                  checked={form.waiverAccepted}
                  onChange={handleChange}
                />
              }
              label="I have read and accept the waiver"
            />
          </Paper>
        )}

        {/* Payment method (installments) */}
        {installmentsOfferable && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>Payment</Typography>
            <FormControl>
              <RadioGroup
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
              >
                <FormControlLabel
                  value="full"
                  control={<Radio />}
                  label={`Pay in full — ${formatMoney(selectedPosition?.priceInCents)}`}
                />
                <FormControlLabel
                  value="installments"
                  control={<Radio />}
                  label="Pay in installments"
                />
              </RadioGroup>
            </FormControl>
            {paymentMethod === 'installments' && (
              <Box sx={{ mt: 1 }}>
                {(() => {
                  const n = config.installmentNumberOfPayments || 0;
                  const weeks = config.installmentIntervalWeeks || 0;
                  const price = selectedPosition?.priceInCents || 0;
                  const base = n ? Math.floor(price / n) : 0;
                  const first = n ? base + (price - base * n) : 0;
                  return (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {n} payments: {formatMoney(first)} charged today, then {n - 1} × {formatMoney(base)} every {weeks} week{weeks === 1 ? '' : 's'}.
                    </Typography>
                  );
                })()}
                <Divider sx={{ mb: 2 }} />
                {squareError && <Alert severity="error" sx={{ mb: 2 }}>{squareError}</Alert>}
                <Typography variant="body2" sx={{ mb: 1 }}>Card details</Typography>
                <Box ref={cardContainerRef} sx={{ minHeight: 90 }} />
                <Typography variant="caption" color="text.secondary">
                  Your card is stored securely by Square. We never see or store your card number.
                </Typography>
              </Box>
            )}
          </Paper>
        )}

        <Button
          type="submit"
          variant="contained"
          size="large"
          disabled={submitting || (config.waiverText && !form.waiverAccepted) || emailMismatch || birthdayInvalid || (paymentMethod === 'installments' && !squareReady)}
          fullWidth
          sx={{ py: 1.5 }}
        >
          {submitting ? <CircularProgress size={24} /> : (paymentMethod === 'installments' ? 'Pay First Installment' : 'Proceed to Payment')}
        </Button>
      </form>

      <Dialog
        open={warningOpen}
        onClose={() => setWarningOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Please Confirm</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              '& p': { mt: 0, mb: 1 },
              '& p:last-child': { mb: 0 },
              '& ul, & ol': { mt: 0, mb: 1, pl: 3 },
              '& a': { color: 'primary.main' },
            }}
            dangerouslySetInnerHTML={{ __html: sanitizedWarning }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWarningOpen(false)} disabled={submitting}>Cancel</Button>
          <Button
            variant="contained"
            disabled={submitting}
            onClick={async () => {
              setWarningOpen(false);
              await submitRegistration();
            }}
          >
            I Confirm &mdash; Proceed to Payment
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={differentEmailDialogOpen}
        onClose={() => setDifferentEmailDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Use a Different Email?</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            To register with a different email address, you&apos;ll need to log out first, then refresh the page (Ctrl+R) before starting a new registration.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDifferentEmailDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => logout()}>
            Log Out
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RegistrationPage;
